import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * MCP クライアント (Claude connector 等) の OAuth 認可の承認ページ。
 * auth-backend の /v1/authorize がここ (/oauth2/authorize/:sessionId) に
 * リダイレクトし、承認するとそのユーザーとしての access token がクライアントに
 * 発行される (docs/mcp.md)。
 *
 * 公式の承認ページ (@backstage/plugin-auth) は新フロントエンドシステム専用の
 * ため、このアプリ (旧システム) 向けに同じ backend API で自前実装している。
 */

interface ConsentSession {
  id: string;
  clientName?: string;
  redirectUri: string;
}

const useStyles = makeStyles(theme => ({
  card: {
    maxWidth: 600,
    margin: '0 auto',
    marginTop: theme.spacing(4),
  },
  callbackUrl: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    fontSize: '0.875rem',
  },
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
}));

export const ConsentPage = () => {
  const classes = useStyles();
  const { sessionId } = useParams<{ sessionId: string }>();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const configApi = useApi(configApiRef);
  const appTitle = configApi.getOptionalString('app.title') ?? 'vega';

  const [session, setSession] = useState<ConsentSession>();
  const [error, setError] = useState<Error>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('auth');
        const res = await fetchApi.fetch(`${baseUrl}/v1/sessions/${sessionId}`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const data = (await res.json()) as ConsentSession;
        if (mounted) {
          setSession(data);
        }
      } catch (e) {
        if (mounted) {
          setError(e as Error);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [discoveryApi, fetchApi, sessionId]);

  const handleAction = useCallback(
    async (action: 'approve' | 'reject') => {
      setSubmitting(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('auth');
        const res = await fetchApi.fetch(
          `${baseUrl}/v1/sessions/${sessionId}/${action}`,
          { method: 'POST' },
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const { redirectUrl } = (await res.json()) as { redirectUrl?: string };
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } catch (e) {
        setError(e as Error);
        setSubmitting(false);
      }
    },
    [discoveryApi, fetchApi, sessionId],
  );

  let content;
  if (error) {
    content = <ResponseErrorPanel error={error} />;
  } else if (!session) {
    content = <Progress />;
  } else {
    content = (
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {session.clientName ?? 'An application'} が {appTitle} への
            アクセスを求めています
          </Typography>
          <Alert severity="warning">
            承認すると、このアプリケーションはあなたとして {appTitle} に
            アクセスできる token を受け取ります。心当たりのないリクエストは
            承認しないでください。
            <Box mt={1}>
              <Typography variant="body2">Callback URL:</Typography>
              <Box className={classes.callbackUrl}>{session.redirectUri}</Box>
            </Box>
          </Alert>
        </CardContent>
        <CardActions className={classes.actions}>
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={() => handleAction('reject')}
          >
            拒否
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={submitting}
            onClick={() => handleAction('approve')}
          >
            {submitting ? '処理中...' : '承認'}
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="アクセス許可のリクエスト" />
      <Content>{content}</Content>
    </Page>
  );
};
